import { fetchJSON as fetchAPI } from './client';

export type ProductHeader = {
  asin: string; parent_asin?: string; sku: string; product_name: string;
  marketplace: string; brand: string; category: string; status: string;
  created_at: string; updated_at: string; image_url?: string;
  badges?: string[]; marketplace_tags?: string[]; compliance_status: string;
  business_owner?: string; child_variations?: number; bookmarked: boolean; favorited: boolean;
};

export type KPIValue = {
  key: string; name: string; value: string; unit?: string; trend?: string;
  benchmark?: string; score?: number; drill_down?: string;
};

export type WorkspaceRecommendation = {
  id: string; title: string; business_impact: string; confidence: number;
  priority: string; evidence?: string[]; expected_revenue?: string; expected_profit?: string;
  expected_time?: string; risk?: string; status: string; action_label: string; action_path: string;
};

export type TimelineEvent = { id: string; type: string; title: string; timestamp: string; actor?: string };

export type ProductNote = { id: string; content: string; pinned: boolean; private: boolean; author: string; created_at: string };

export type AIWorkspaceFrame = {
  conversation_enabled: boolean; evidence_enabled: boolean; context_asin: string;
  placeholder_actions?: string[];
};

export type KnowledgeGraphFrame = {
  asin: string; node_types: string[]; relationship_types: string[]; placeholder: boolean;
};

export type WorkspaceTab = { key: string; name: string; route: string; description?: string; status: string; configurable?: boolean };

export type QuickAction = { key: string; name: string; command: string; permission?: string };

export type ProductWorkspace = {
  header: ProductHeader; kpis: KPIValue[]; recommendations: WorkspaceRecommendation[];
  timeline: TimelineEvent[]; notes: ProductNote[];
  ai_frame: AIWorkspaceFrame; knowledge_frame: KnowledgeGraphFrame;
  active_tab: string; generated_at: string;
};

export type SearchResult = { key: string; label: string; type: string; asin?: string; path: string };

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const productWorkspaceApi = {
  workspace: (asin: string) => fetchCmd<ProductWorkspace>(`/asin360/${encodeURIComponent(asin)}/workspace`),
  summary: (asin: string) => fetchCmd<{ header: ProductHeader; kpis: KPIValue[] }>(`/asin360/${encodeURIComponent(asin)}/summary`),
  timeline: (asin: string) => fetchCmd<{ timeline: TimelineEvent[] }>(`/asin360/${encodeURIComponent(asin)}/timeline`).then((r) => r.timeline),
  recommendations: (asin: string) => fetchCmd<{ recommendations: WorkspaceRecommendation[] }>(`/asin360/${encodeURIComponent(asin)}/recommendations`).then((r) => r.recommendations),
  notes: (asin: string) => fetchCmd<{ notes: ProductNote[] }>(`/asin360/${encodeURIComponent(asin)}/notes`).then((r) => r.notes),
  quickActions: (asin: string) => fetchCmd<{ actions: QuickAction[] }>(`/asin360/${encodeURIComponent(asin)}/quick-actions`).then((r) => r.actions),
  navigation: () => fetchCmd<{ tabs: WorkspaceTab[] }>('/asin360/navigation').then((r) => r.tabs),
  search: (query: string) =>
    fetchCmd<{ results: SearchResult[] }>('/asin360/search', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }),
    }).then((r) => r.results),
  aiFrame: (asin: string) => fetchCmd<AIWorkspaceFrame>(`/asin360/${encodeURIComponent(asin)}/ai`),
  knowledge: (asin: string) => fetchCmd<KnowledgeGraphFrame>(`/asin360/${encodeURIComponent(asin)}/knowledge`),
};
